/**
 * users 域 Zod 共享契约 — ADM-USR-S001~S006
 */

import { z } from 'zod';
import { LANGUAGE_CODE_RE } from './system-config';

/**
 * 累计充值单币种聚合行（ADM-USR `total_spend` / `net_spend` 的元素）。
 * 契约 `02-API契约/A3-运营用户.md` §1：按 `purchase_amounts.paid_currency` **原币分组**、MVP 不做汇率归一。
 * 聚合口径 SSOT 见 `03-数据字典/60-订阅.md` §8（gross=ΣPURCHASE / net=max(0,gross−refunded)，同币种内）。
 * - currency：原币 ISO 4217（EUR / PLN / …）
 * - amount_minor_units：该币种 minor units（**bigint → string**，防精度丢失）
 */
export type CurrencySpend = {
  currency: string;
  amount_minor_units: string;
};

/**
 * 用户列表查询参数（ADM-USR-S006）— 分页 + App 维度 + 账号状态 + 关键字搜索 + 地区筛选
 *
 * 契约源：dev-docs 提案 `2026-05-31-admin-gap-domains-contract.md` ① S006 行（张培已 review
 * 2026-05-31）明确支持「按 App 维度、账号状态、地区筛选，并支持用户名 / 邮箱 / 用户 ID 关键字搜索」。
 * 分页用 page/limit（offset 语义），与原型 users.jsx 页码器一致；limit 硬上限 100。
 * status 闭集：account 账号状态（normal / risk / banned），与原型 tab 一致。
 *
 * 服务端下推（修 P1 回归：前端原本仅在单页内 client-side 过滤，现下推全量）：
 * - q：用户名（display_name）/ 邮箱（email）关键字搜索；UUID 用户 ID 精确搜索；展示用户 ID
 *      规范化后精确搜索（见 service 实现）。展示 ID 仅用于展示和检索，不替代 UUID 资源路径。
 * - country：地区多选（最近一次 session 国家），重复 query 参数；ISO 3166-1 alpha-2。
 * - language：当前内容语言多选（users.last_seen_language，口径同详情 A3 §7.1），重复 query 参数；
 *      命中任一即入选，从未上报（NULL）的用户不命中任何语言筛选（A3 §5.1）。
 * 订阅维度筛选（A3 §5.1 `subscription_status`）不在本 schema；前端「订阅」筛选按当前页客户端筛。
 */
export const UserListQuerySchema = z.object({
  /** App 维度（users.app_id 归属）；缺省 = 全部 App */
  app_id: z.string().uuid().optional(),
  /** 账号状态：normal=正常 / risk=风控观察 / banned=已封禁；缺省 = 全部 */
  status: z.enum(['normal', 'risk', 'banned']).optional(),
  /** 关键字搜索：display_name / email ILIKE '%q%'；合法 UUID 和规范化展示 ID 均额外精确匹配 */
  q: z.string().trim().min(1).max(100).optional(),
  /**
   * 付费状态（admin 扩展，ADM-USR-S006）：paid=有成功充值 / unpaid=无成功充值；缺省 = 不限。
   * 口径与列表 `total_spend` 同源——原生 + Web 联合事实里存在 Production 的 PURCHASE（金额 > 0）即付费。
   */
  paid_status: z.enum(['paid', 'unpaid']).optional(),
  /** 地区多选（最近一次 session 国家，ISO 3166-1 alpha-2）；重复 query 参数 → 数组 */
  country: z.array(z.string().length(2)).optional(),
  /** 当前内容语言多选（users.last_seen_language，A3 §5.1）；重复 query 参数 → 数组；格式非法 → VALIDATION_FAILED */
  language: z.array(z.string().regex(LANGUAGE_CODE_RE)).optional(),
  /**
   * 标签人群筛选（ADM-USR-S009）：重复 query `tag_ids` → UUID 数组。
   * 只接受已绑定且 is_active=true 的成员；任一不可用 → VALIDATION_FAILED（服务端硬校验）。
   */
  tag_ids: z.array(z.string().uuid()).optional(),
  /** 页码，从 1 开始 */
  page: z.coerce.number().int().min(1).default(1),
  /** 每页条数，硬上限 100（列表上限） */
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type UserListQuery = z.infer<typeof UserListQuerySchema>;

/**
 * 用户列表项（ADM-USR-S006）— snake_case 规范化 DTO
 *
 * 字段映射（DB → API）：
 * - user_id           ← users.id（UUID 资源 ID；所有 `/:id` 路径仍只接受它）
 * - display_user_id   ← users.display_user_id（只读展示/检索 ID，绝不替代 UUID 路径）
 * - display_name      ← users.display_name
 * - email             ← users.email
 * - app_id            ← users.app_id（权威归属：NOT NULL、per-App 身份不可变，server #323）
 * - country           ← user_sessions.country（最近一次 session 的国家，ISO 3166-1 alpha-2）
 * - language          ← users.last_seen_language（当前内容语言，口径同详情 A3 §7.1；从未上报 → null，不兜底 'en'）
 * - status            ← users_admin_extensions.status（'ACTIVE' / 'BANNED'，缺扩展行默认 ACTIVE）
 * - risk_status       ← users_admin_extensions.risk_status（'NONE' / 'FLAGGED' / 'RESTRICTED'）
 * - risk_reason       ← users_admin_extensions.risk_reason
 * - coin_balance      ← wallets.coin_balance（bigint → string，可为负）
 * - bonus_balance     ← wallets.bonus_balance（bigint → string，可为负）
 * - total_spend       ← 累计充值毛额，**按 `paid_currency` 原币分组数组** `[{ currency, amount_minor_units }]`
 *                       （catch-up dev-docs #130：契约 A3 §1 / 60-订阅 §8）。读 `purchase_amounts` 原币列聚合
 *                       gross=ΣPURCHASE GROUP BY paid_currency。无充值 → `[]`；数据源不可得 → `null`。
 * - net_spend         ← 净充值（同币种内 max(0, gross − refunded)，该币种 net=0 不入数组）；shape 同 total_spend
 * - created_at        ← users.created_at（注册时间）
 * - last_login_at     ← users.last_login_at（最近活跃，可空）
 */
export type UserListItem = {
  user_id: string;
  display_user_id: string;
  display_name: string | null;
  email: string | null;
  app_id: string | null;
  country: string | null;
  /** 当前内容语言（A3 §5.2，口径同 §7.1）；从未上报 → null */
  language: string | null;
  status: 'ACTIVE' | 'BANNED';
  risk_status: 'NONE' | 'FLAGGED' | 'RESTRICTED';
  risk_reason: string | null;
  coin_balance: string;
  bonus_balance: string;
  /** 累计充值毛额，按 paid_currency 原币分组（A3 §1）；无充值 `[]`、数据源不可得 `null` */
  total_spend: CurrencySpend[] | null;
  /** 净充值（同币种内 max(0, gross−refunded)）；shape 同 total_spend */
  net_spend: CurrencySpend[] | null;
  created_at: string;
  last_login_at: string | null;
};

/** 用户列表分页响应（ADM-USR-S006）— offset 分页（page/limit/total） */
export type UserListResponse = {
  items: UserListItem[];
  total: number;
  page: number;
  limit: number;
};

export const BanUserRequestSchema = z.object({
  reason: z.string().min(1).max(500),
});
export type BanUserRequest = z.infer<typeof BanUserRequestSchema>;

export const UnbanUserRequestSchema = z.object({});
export type UnbanUserRequest = z.infer<typeof UnbanUserRequestSchema>;

export const AddDeviceWhitelistRequestSchema = z.object({
  /** 白名单所属 App（canonical key = (app_id, device_id)，PR332-001）；必填，缺失 → VALIDATION_FAILED */
  app_id: z.string().uuid(),
  device_id: z.string().min(1).max(64),
  /** 加白原因（运营内部备注，可空）；契约 note→reason（A3 §9.2）*/
  reason: z.string().max(500).nullable().optional(),
});
export type AddDeviceWhitelistRequest = z.infer<typeof AddDeviceWhitelistRequestSchema>;

/**
 * 当前订阅状态摘要（ADM-USR-S002 订阅计划状态）— 取 subscription_entitlements 中 status='ACTIVE'
 * 且 deleted_at IS NULL 的最新一笔；无活跃订阅 → null。
 */
export type SubscriptionStatusSummary = {
  /** ACTIVE / IN_GRACE / PAUSED / EXPIRED / REFUNDED / CANCELLED（此处恒为活跃口径取到的值）*/
  status: string;
  entitlement_id: string;
  product_id: string;
  store: string;
  auto_renew: boolean;
  is_in_trial: boolean;
  starts_at: string;
  expires_at: string;
  renewal_count: number;
  amount_minor_units: string;
  currency: string;
};

export type AdminUserDetailResponse = {
  /** 当前 App 的全部持有标签，包含未绑定及禁用定义；不套人群过滤。 */
  tags: {
    id: string;
    tag_key: string;
    name: string;
    binding_status: 'bound' | 'unbound';
    is_active: boolean;
  }[];
  /** UUID 资源 ID；所有 `/:id` 路径仍只接受该值。 */
  id: string;
  /** 只读展示/检索 ID；不替代 UUID 资源路径。 */
  display_user_id: string;
  email: string | null;
  /**
   * 邮箱验证/绑定时间（users.email_verified_at → ISO；未验证/未绑定 → null）。
   * 绑定态**仅**由本字段判定：`email` 非空**不**代表已绑定（可为未验证的当前邮箱），
   * 仅 `email_verified_at IS NOT NULL` 属已绑定/已验证（A3 §5.2 + 03-数据字典/10-用户.md §1.1，dev-docs #450）。
   */
  email_verified_at: string | null;
  display_name: string | null;
  is_anonymous: boolean;
  status: 'ACTIVE' | 'BANNED';
  banned_at: string | null;
  banned_reason: string | null;
  /**
   * 风控状态 / 触发原因（ADM-USR-S002）。源 users_admin_extensions.risk_status/risk_reason，
   * 但 C 端风控 → admin ingestion 尚未接入（无 C 端 per-user 风控数据源），故当前恒为
   * 'NONE' / null（占位）。真值待 C 端风控能力 + 反向补 dev-docs 契约后填充（goal carve-out）。
   */
  risk_status: 'NONE' | 'FLAGGED' | 'RESTRICTED';
  risk_reason: string | null;
  /** 归属 App（ADM-USR-S002）：用户最近一次 session 的 app_id / app 名；无 session → null */
  app_id: string | null;
  app_name: string | null;
  /**
   * 当前内容语言（ADM-USR-S002，契约 A3 §7.1）← users.last_seen_language 原样返回：用户最近一次请求
   * 归一后的内容语言（后到覆盖）。从未上报 → null，不代入 C 端异步触达用的 'en' 兜底；
   * 不是注册时语言，也不是设备系统语言。
   */
  language: string | null;
  /**
   * 注册语言（ADM-USR-S002，契约 A3 §7.1）← users.registration_language 原样返回：创建该 user 时
   * 归一后的内容语言，C 端建号同事务写入、此后不变。未记录（本列上线前创建的存量 user）→ null。
   */
  registration_language: string | null;
  /**
   * 最近设备 ID（ADM-USR-S002）：用户最近一次 session 的 `user_sessions.device_id`（已镜像列，只读聚合）。
   * 与 last_login_ip 同口径展示用户最近设备标识；无 session 或该 session 未携带 device_id → null。
   */
  device_id: string | null;
  /**
   * 实际阅读章节数（ADM-USR-S002，契约 A3 §4，张培 #320 决议按 user_id 聚合）。
   * = Σ `reading_sessions.chapters_read`（`03-数据字典/80-任务.md` §2 / §2.1，按 user_id 跨业务日
   * date_key 汇总）。**语义是「当日读过」而不是「读完」**：heartbeat `outcome='ACCEPTED'` 且
   * `accepted_seconds > 0` 即计入，每章每业务日最多 +1 —— 展示时不可当作完读量。
   * 契约明文**不对历史业务日回填**，故本值只覆盖 C 端补齐写入之后的 date_key，与全历史口径的
   * `paid_unlocked_chapter_count` **不同期**（老用户可能出现前者小于后者，不是 bug）。
   * 计数类整数直出（章节量远小于 JS 安全整数上限）；从未阅读 → `0`（非 null，计数有确定下界）。
   * 禁用 reading_progress.last_chapter_index 跨书相加（per-book 续读游标、会高估）。
   */
  read_chapter_count: number;
  /**
   * 付费解锁章节数（ADM-USR-S002，契约 A3 §4）。
   * = COUNT(有效 `unlock_records`，deleted_at IS NULL AND unlock_method=PAID)（`03-数据字典/40-阅读与解锁.md` §1）。
   * **不** JOIN `chapters.is_free`：该列随 `free_chapter_count` 调整 / 手工营销免费而变，按当前值
   * 过滤会抹掉历史付费事实；仅 `unlock_method=PAID` 的解锁行为付费事实（AD 永久权益排除）。
   * 退款不软删 unlock_records → 存在解锁行即计有效；章节软删不影响历史付费解锁计数（不滤 chapters.deleted_at）。
   * 从未付费解锁 → `0`（非 null）。与 read_chapter_count 口径独立、不可互替。
   */
  paid_unlocked_chapter_count: number;
  /** 当前订阅状态摘要；无活跃订阅 → null */
  subscription_status: SubscriptionStatusSummary | null;
  /**
   * 累计充值毛额 / 净充值，按 `paid_currency` 原币分组（ADM-USR-S002 充值记录；与 UserListItem 同 shape）。
   * catch-up dev-docs #130（契约 A3 §1 / 60-订阅 §8）：读 `purchase_amounts` 原币列 gross=ΣPURCHASE、
   * net=max(0,gross−refunded)，GROUP BY paid_currency，MVP 不做汇率归一。无充值 `[]`、数据源不可得 `null`。
   */
  total_spend: CurrencySpend[] | null;
  net_spend: CurrencySpend[] | null;
  last_login_at: string | null;
  last_login_ip: string | null;
  created_at: string;
};

/** v1已发布Coin入账兼容行，保持真实资产数量及旧字段，至少保留v2上线后6个月。 */
export type LegacyUserRechargeRecordItem = {
  id: string;
  app_id: string | null;
  vendor_product_id: string | null;
  coin_amount: string;
  created_at: string;
};

/** 充值历史为原币财务事实；Web商品描述取成交快照，不使用到账Coin数量。 */
export type UserRechargeRecordItem = {
  id: string;
  occurred_at: string;
  kind: 'PURCHASE' | 'REFUND';
  amount_minor_units: string;
  currency: string;
  environment: 'Production' | 'Sandbox' | 'Unknown';
  description: string | null;
};

/**
 * v1 已发布消费记录兼容行（A3 §7.2「消费记录 v1 兼容」）— 源 unlock_records 章节解锁快照，
 * 保持旧字段不删不改名，至少保留至 v2 上线后 6 个月。
 */
export type LegacyUserConsumeRecordItem = {
  id: string;
  novel_title: string;
  chapter_index: number;
  chapter_title: string;
  /** 本次解锁消耗 coin（unlock_records.coin_used；纯 Bonus 解锁为 '0'）*/
  coin_used: string;
  /** 本次解锁消耗 bonus（unlock_records.bonus_used）*/
  bonus_used: string;
  created_at: string;
};

/**
 * v2 消费记录项（ADM-USR-S002，契约 A3 §7.2）— 按资产维度合并 Coin / Bonus 消耗。
 *
 * 字段映射（DB → API）：
 * - COIN 行 ← coin_transactions（direction=DEBIT，排除 amount=0 的 REFUND_TOMBSTONE 占位）：
 *   id / amount / source / created_at；description = 章节解锁取 metadata 书名 · 第{chapter_index}章快照，
 *   ADMIN_ADJUST / ADMIN_TRANSFER 取 metadata.reason 快照，其余 null
 * - BONUS 行 ← bonus_consumptions：id / amount / reason → source / created_at；description = title 快照
 */
export type UserConsumeRecordItem = {
  id: string;
  asset_type: 'COIN' | 'BONUS';
  /** 消耗数量（资产数量，bigint → string，无 currency）*/
  amount: string;
  /** Coin 行 = coin_transactions.source；Bonus 行 = bonus_consumptions.reason；随钱包字典闭集扩展 */
  source: string;
  occurred_at: string;
  description: string | null;
};

/**
 * 章节阅读记录项（ADM-USR-S002，契约 A3 §7.3）— 一行 = 该用户某业务日读过的一章（当日读过，不表示读完）。
 *
 * 字段映射（DB → API）：
 * - id / date_key / read_at / chapter_id ← reading_session_chapters.id / date_key / created_at / chapter_id
 * - chapter_index / chapter_title / novel_id ← chapters.chapter_index / title / novel_id（章节行不存在 → null）
 * - novel_title ← novels.title（关联行不存在 → null）
 */
export type UserChapterReadingRecordItem = {
  id: string;
  /** 业务日 YYYY-MM-DD（按 users.day_cut_timezone 切日）*/
  date_key: string;
  read_at: string;
  chapter_id: string;
  chapter_index: number | null;
  chapter_title: string | null;
  novel_id: string | null;
  novel_title: string | null;
};

/**
 * 章节解锁记录项（ADM-USR-S002，契约 A3 §7.3）— 源 unlock_records（仅付费解锁；AD 排除；免费 / 限免 / 小时卡 / VIP / 买断不产生行）。
 * 书名 / 章节序号 / 章节标题取解锁时快照列；coin_used / bonus_used 为资产数量（bigint → string，无 currency）。
 */
export type UserChapterUnlockRecordItem = {
  id: string;
  unlocked_at: string;
  novel_id: string;
  novel_title: string;
  chapter_id: string;
  chapter_index: number;
  chapter_title: string;
  coin_used: string;
  bonus_used: string;
  auto_unlocked: boolean;
};

/**
 * 小说阅读记录项（ADM-USR-S002，契约 A3 §7.3）— 源 reading_progress，每本书一行续读位置。
 *
 * 字段映射（DB → API）：
 * - id / novel_id / last_chapter_id / last_chapter_index ← reading_progress 同名列
 * - first_read_at / last_read_at ← reading_progress.created_at / last_read_at
 * - novel_title / novel_locale / novel_total_chapters ← novels.title / locale / total_chapters（关联行不存在 → null）
 * - last_chapter_title ← chapters.title（章节行不存在 → null）
 */
export type UserNovelReadingRecordItem = {
  id: string;
  novel_id: string;
  novel_title: string | null;
  novel_locale: string | null;
  novel_total_chapters: number | null;
  last_chapter_id: string;
  last_chapter_index: number;
  last_chapter_title: string | null;
  first_read_at: string;
  last_read_at: string;
};

export type BannedUserListItem = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  banned_at: string;
  banned_reason: string | null;
};

export type DeviceWhitelistResponse = {
  id: string;
  /** 白名单所属 App（canonical key = (app_id, device_id)，PR332-001）*/
  app_id: string;
  device_id: string;
  /** 加白原因（运营内部备注，可空）；契约 note→reason（A3 §9）*/
  reason: string | null;
  created_by: string | null;
  created_at: string;
};

export type UsersStatsResponse = {
  scope: 'all' | 'app';
  app_id: string | null;
  total_users: number;
  active_users: number;
  new_users: number;
};

/** 预置标签手动关联响应，App 从目标用户行推导。 */
export type UserTagAssignmentResponse = {
  app_id: string;
  user_id: string;
  tag_id: string;
  is_assigned: boolean;
};
